// Czysty moduł zakładki „Statystyki” (spec/statystyki.md): serie, zakresy presetów, skala osi Y,
// przerzedzanie etykiet — bez DOM, testowane w tests/unit/stats_core_test.ts.
import { localDateISO } from './dates.ts';

export interface StatsRowLike { period: string; created: number; completed: number; cancelled: number; deleted: number }
export type StatsBucket = 'day' | 'week' | 'month';

/** Serie w stałej kolejności i kolorze (paleta kategoryczna, zwalidowana pod CVD). */
export const SERIES: { key: keyof Omit<StatsRowLike, 'period'>; label: string; color: string }[] = [
  { key: 'created', label: 'dodane', color: '#2a78d6' },
  { key: 'completed', label: 'wykonane', color: '#1baf7a' },
  { key: 'cancelled', label: 'anulowane', color: '#eda100' },
  { key: 'deleted', label: 'usunięte', color: '#eb6834' },
];

export type RangePreset = '30d' | '90d' | '12m' | 'custom';

const isoLocal = (d: Date): string => localDateISO(d);

/** Zakres dla presetu (do dziś włącznie). */
export function presetRange(preset: RangePreset, today = new Date()): { from: string; to: string } {
  const to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const from = new Date(to);
  if (preset === '30d') from.setDate(from.getDate() - 29);
  else if (preset === '90d') from.setDate(from.getDate() - 89);
  else { from.setMonth(from.getMonth() - 11); from.setDate(1); }
  return { from: isoLocal(from), to: isoLocal(to) };
}

/** Preset dobrany do kubełka (spec: zmiana kubełka dobiera sensowny zakres). */
export function presetForBucket(bucket: StatsBucket): RangePreset {
  return bucket === 'day' ? '30d' : bucket === 'week' ? '90d' : '12m';
}

/** Etykieta okresu na osi / w tabeli. */
export function periodLabel(period: string, bucket: StatsBucket): string {
  const [y, m, d] = period.split('-');
  if (bucket === 'month') return `${m}.${y}`;
  return `${d}.${m}`;
}

/** Indeksy etykiet X do pokazania, żeby się nie nakładały: co k-ta, zawsze pierwsza i ostatnia
 *  (przedostatnia z siatki znika, gdy stałaby za blisko ostatniej). */
export function labelIndices(n: number, maxLabels: number): Set<number> {
  const out = new Set<number>();
  if (n === 0) return out;
  const step = Math.max(1, Math.ceil(n / Math.max(1, maxLabels)));
  for (let i = 0; i < n; i += step) out.add(i);
  const lastGrid = Math.floor((n - 1) / step) * step;
  // ostatnia etykieta z siatki stałaby bliżej niż zwykły odstęp `step` → znika na rzecz ostatniego okresu
  if (lastGrid !== n - 1 && lastGrid !== 0) out.delete(lastGrid);
  out.add(n - 1);
  return out;
}

/** Skala osi Y: całkowity krok (1, 2, 5, 10, 20, 50…) i górna granica = wielokrotność kroku ≥ max. */
export function yScale(max: number, maxTicks = 5): { step: number; yMax: number } {
  if (max <= 0) return { step: 1, yMax: 1 };
  const raw = max / maxTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  let step = 10 * pow;
  for (const m of [1, 2, 5, 10]) { if (m * pow >= raw) { step = m * pow; break; } }
  step = Math.max(1, Math.round(step));
  return { step, yMax: Math.ceil(max / step) * step };
}

